import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@telegram-apps/telegram-ui";
import { Stage, Objective } from "../../handlers/types";
import ApiService from "../../handlers/apiService";
import { t } from "../../utils/i18n/index";
import { useGameStore, selectEnergy } from "../../store/gameStore";
import { useModalManager, ModalType } from "../../utils/modal/ModalManager";
import Game from "../../scripts/game/Game";
import "./StageSelect.css";

// Skeleton component for loading state
function StageSkeleton() {
  return (
    <div className="stage-card stage-card--skeleton">
      {/* Stage Title Skeleton */}
      <div className="stage-card__title skeleton skeleton--title"></div>

      {/* Image and Description Container Skeleton */}
      <div className="stage-card__content">
        <div className="stage-card__image-container">
          <div className="stage-card__image skeleton skeleton--image"></div>
        </div>
        <div className="stage-card__description skeleton skeleton--description">
          <div className="skeleton skeleton--text skeleton--text-line-1"></div>
          <div className="skeleton skeleton--text skeleton--text-line-2"></div>
        </div>
      </div>

      {/* Mana Bonus Skeleton */}
      <div className="stage-card__mana-bonus">
        <div className="skeleton skeleton--mana-text"></div>
        <div className="skeleton skeleton--mana-icon"></div>
        <div className="skeleton skeleton--mana-value"></div>
      </div>

      {/* Objectives Skeleton */}
      <div className="stage-card__objectives">
        <div className="stage-card__objectives-title skeleton skeleton--objectives-title"></div>
        <div className="stage-card__objectives-list">
          <div className="stage-card__objective">
            <div className="skeleton skeleton--objective-prefix"></div>
            <div className="skeleton skeleton--objective-text"></div>
            <div className="skeleton skeleton--objective-reward"></div>
          </div>
        </div>
      </div>

      {/* Play Button Skeleton */}
      <div className="stage-card__play-button skeleton skeleton--button"></div>
    </div>
  );
}

interface StageCardProps {
  stage: Stage;
  onPlayStage: (stageId: string) => void;
}

function StageCard({ stage, onPlayStage }: StageCardProps) {
  const { openModal } = useModalManager();
  const energy = useGameStore(selectEnergy);
  const [isHovered, setIsHovered] = useState(false);
  const stageObjectives = stage.objectives || [];

  const handlePlayClick = useCallback(() => {
    if (energy > 0) {
      onPlayStage(stage.id);
    } else {
      openModal(ModalType.ENERGY_POPUP, {});
    }
  }, [stage.id, onPlayStage, openModal, energy]);

  const stageTitleText = stage.titleKey
    ? t(stage.titleKey)
    : `Stage ${stage.id}`;

  // Get manaBonus from the actual API response format
  const manaBonus = (stage as any).manaBonus || 0;

  return (
    <div
      className={`stage-card ${!stage.unlocked ? "stage-card--locked" : ""}`}
    >
      {/* Stage Title */}
      <h3 className="stage-card__title">{stageTitleText}</h3>

      {/* Image and Description Container */}
      <div className="stage-card__content">
        <div className="stage-card__image-container">
          <img
            src={`/assets/img/levels/${
              stage.image || `${stage.id.toLowerCase()}_background`
            }.webp`}
            alt={stageTitleText}
            className={`stage-card__image ${
              !stage.unlocked ? "stage-card__image--locked" : ""
            }`}
          />
          {!stage.unlocked && (
            <div className="stage-card__lock-icon">
              <img
                src="/assets/img/ui/lock.webp"
                alt="Locked"
                className="stage-card__lock-image"
              />
            </div>
          )}
        </div>

        <div className="stage-card__description">
          {stage.descriptionKey ? t(stage.descriptionKey) : ""}
        </div>
      </div>

      {/* Mana Bonus Section */}
      {stage.unlocked && manaBonus > 0 && (
        <div className="stage-card__mana-bonus">
          <span className="stage-card__mana-bonus-text">
            {t("common.manaBonus")}
          </span>
          <img
            src="/assets/img/ui/mana.webp"
            alt="Mana"
            className="stage-card__mana-icon"
          />
          <span className="stage-card__mana-bonus-value">{manaBonus}/day</span>
        </div>
      )}

      {/* Objectives Section */}
      {stage.unlocked && stageObjectives.length > 0 && (
        <div className="stage-card__objectives">
          <h4 className="stage-card__objectives-title">
            {t("common.objectives")}
          </h4>
          <div className="stage-card__objectives-list">
            {stageObjectives.map((objective: Objective, index: number) => (
              <div
                key={index}
                className={`stage-card__objective ${
                  objective.completed ? "stage-card__objective--completed" : ""
                }`}
              >
                <span className="stage-card__objective-prefix">
                  {objective.completed ? "✓" : "○"}
                </span>
                <span className="stage-card__objective-text">
                  {t(objective.descriptionKey) ||
                    objective.description ||
                    "Unknown objective"}
                </span>
                {objective.prize > 0 && (
                  <div className="stage-card__objective-reward">
                    <span className="stage-card__objective-reward-text">
                      ({objective.prize}
                    </span>
                    <img
                      src="/assets/img/coin.webp"
                      alt="Coin"
                      className="stage-card__objective-coin"
                    />
                    <span className="stage-card__objective-reward-text">)</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Play Button */}
      {stage.unlocked && (
        <Button
          size="s"
          mode="filled"
          className="stage-card__play-button"
          onClick={handlePlayClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {t("common.play")}
        </Button>
      )}
    </div>
  );
}

interface CharacterUpgradeCardProps {
  onPlayNextLevel: (stageId: string) => void;
  nextLevelStage: Stage | null;
}

function CharacterUpgradeCard({
  onPlayNextLevel,
  nextLevelStage,
}: CharacterUpgradeCardProps) {
  const handleClick = useCallback(() => {
    if (nextLevelStage) {
      onPlayNextLevel(nextLevelStage.id);
    }
  }, [nextLevelStage, onPlayNextLevel]);

  if (!nextLevelStage) {
    return null;
  }

  return (
    <Button
      size="s"
      mode="filled"
      className="character-upgrade-card__button"
      onClick={handleClick}
    >
      {t("menu.playNextLevel")}
    </Button>
  );
}

export function StageSelect(): JSX.Element {
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const apiService = ApiService.getInstance();

  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      setLoading(true);
      setError(null);
      const stagesData = await apiService.api.stagesList();
      setStages(stagesData || []);
    } catch (err) {
      console.error("Failed to fetch stages:", err);
      setError(t("errors.failedToLoadStages"));
    } finally {
      setLoading(false);
    }
  };

  const handlePlayStage = useCallback(
    (stageId: string) => {
      navigate(`/game?stage=${stageId}`);
    },
    [navigate]
  );

  // Find the next level to play
  const nextLevelStage = useMemo(() => {
    const unlockedStages = stages.filter((stage) => stage.unlocked);
    if (unlockedStages.length === 0) return null;

    // Debug: Log the stages to understand their structure and ordering
    console.log("=== DEBUGGING STAGE SELECTION ===");
    console.log(
      "All stages:",
      stages.map((s) => ({
        id: s.id,
        unlocked: s.unlocked,
        objectives: s.objectives,
      }))
    );
    console.log(
      "Unlocked stages:",
      unlockedStages.map((s) => ({ id: s.id, objectives: s.objectives }))
    );

    // Helper function to parse stage ID (e.g., "D1" -> { prefix: "D", number: 1 })
    const parseStageId = (id: string) => {
      const match = id.match(/^([A-Z]+)(\d+)$/);
      if (!match) {
        console.warn(`Could not parse stage ID: ${id}`);
        return { prefix: id, number: 0 };
      }
      return { prefix: match[1], number: parseInt(match[2], 10) };
    };

    // Strategy 1: Find the first stage where not all objectives are completed
    // This would be the "next" level to progress in
    const incompleteStage = unlockedStages.find((stage) => {
      if (!stage.objectives || stage.objectives.length === 0) {
        return true; // No objectives means it can be played
      }
      return stage.objectives.some((objective) => !objective.completed);
    });

    if (incompleteStage) {
      console.log("Found incomplete stage:", incompleteStage.id);
      return incompleteStage;
    }

    // Strategy 2: If all unlocked stages are complete, return the highest level
    // Sort stages by prefix and number
    const sortedStages = unlockedStages.sort((a, b) => {
      const stageA = parseStageId(a.id);
      const stageB = parseStageId(b.id);

      // First sort by prefix (D < J < C, etc.)
      if (stageA.prefix !== stageB.prefix) {
        return stageA.prefix.localeCompare(stageB.prefix);
      }

      // Then by number (higher number = later level)
      return stageB.number - stageA.number;
    });

    console.log(
      "Sorted stages:",
      sortedStages.map((s) => s.id)
    );
    console.log("Selected stage:", sortedStages[0]?.id);

    return sortedStages[0];
  }, [stages]);

  return (
    <div className="stage-select" ref={containerRef}>
      {/* Header */}
      <div className="stage-select__header">
        <h1 className="stage-select__title">{t("menu.stageSelect")}</h1>
        <p className="stage-select__subtitle">{t("game.huntForCoins")}</p>
      </div>

      {/* Next Level Card - outside of the list background */}
      <div className="stage-select__next-level-container">
        <CharacterUpgradeCard
          onPlayNextLevel={handlePlayStage}
          nextLevelStage={nextLevelStage}
        />
      </div>

      {/* Cards Container */}
      <div className="stage-select__cards-container" ref={scrollRef}>
        <div className="stage-select__cards">
          {/* All Levels Subheader */}
          {!loading && !error && stages.length > 0 && (
            <div className="stage-select__section-header">
              <h2 className="stage-select__section-title">
                {t("menu.allLevels")}
              </h2>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <>
              <StageSkeleton />
              <StageSkeleton />
              <StageSkeleton />
              <StageSkeleton />
              <StageSkeleton />
            </>
          )}

          {/* Error State */}
          {error && <div className="stage-select__error">{error}</div>}

          {/* Stage Cards */}
          {!loading && !error && stages.length === 0 && (
            <div className="stage-select__error">
              {t("errors.failedToLoadStages")}
            </div>
          )}

          {!loading &&
            !error &&
            stages.map((stage) => (
              <StageCard
                key={stage.id}
                stage={stage}
                onPlayStage={handlePlayStage}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
